import React, {useMemo, forwardRef} from 'react';
import {Tabs} from 'antd-mobile';
import {useTranslation, Trans} from 'react-i18next';
import TeamTableHead, {TeamTableScrollRef} from './team-table-head';
import {TeamFilter, DateFilter} from '../components/filter';
import Tier from '../components/tier';
import SearchInput from '../components/search-input';
import styles from './my-teams.module.scss';
import {cn} from '@/utils';
export interface MyTeamsProps {
  searching?: boolean;
  tier: string;
  dateRange?: [number, number];
  onChangeDateRange?: (startDate?: number, endDate?: number) => void;
  onChangeTier?: (tier: string) => void;
  onChangeSort?: (sortMap: Record<string, any>) => void;
  onScroll?: (scrollLeft: number) => void;
  isEmpty?: boolean;
  // 搜索相关props
  searchValue?: string;
  onSearchChange?: (value: string) => void;
  onSearch?: () => void;
  onAutoSearch?: () => void;
  isSearching?: boolean;
  resultCount?: number;
  showMinCharsWarning?: boolean;
}

const MyTeams = forwardRef<TeamTableScrollRef, MyTeamsProps>(
  (
    {
      tier,
      searching,
      dateRange,
      onChangeSort: _onChangeSort,
      onScroll: _onScroll,
      onChangeTier,
      onChangeDateRange,
      isEmpty,
      searchValue,
      onSearchChange,
      onSearch,
      onAutoSearch,
      isSearching,
      resultCount,
      showMinCharsWarning,
    },
    ref,
  ) => {
    const {t} = useTranslation();

    const headTitles = useMemo(() => {
      return [
        {
          columnKey: 'userId',
          text: t('agent.team-report.user-id'),
          hasSort: false,
          className: 'w-[7.5rem] justify-center',
        },
        {
          columnKey: 'userSort',
          text: t('agent.team-report.user'),
          hasSort: true,
          className: 'w-14 px-3',
        },
        {
          columnKey: 'rechargeSort',
          text: t('agent.team-report.recharge'),
          hasSort: true,
          className: 'w-[6rem] justify-center',
        },
        {
          columnKey: 'bettingSort',
          text: t('agent.team-report.betting'),
          hasSort: true,
          className: 'w-[6rem] justify-center',
        },
        {
          columnKey: 'commissionSort',
          text: t('agent.team-report.commission'),
          hasSort: true,
          className: 'w-[6rem] justify-center',
        },
      ];
    }, [t]);

    const tabOptions = useMemo(() => {
      return Array(4)
        .fill(0)
        .map((_, i) => ({
          key: `${i + 1}`,
          title: <Tier tier={`${i + 1}`} className="w-16 h-7 text-xs" />,
        }));
    }, []);

    return (
      <>
        <TeamTableHead
          ref={ref}
          className={'sticky z-10 top-0 bg-white'}
          topContent={
            <>
              <div className="flex flex-row px-3 pt-3 bg-white justify-between items-center">
                <span className="text-sm text-gray-800 font-bold">
                  {t('agent.team-report.my-teams')}
                </span>
                <div className="flex flex-row gap-x-1">
                  <TeamFilter
                    dateRange={dateRange}
                    onChangeDateRange={onChangeDateRange}
                  />
                  <DateFilter
                    startDate={dateRange?.[0] || Date.now()}
                    endDate={dateRange?.[1] || Date.now()}
                    onDateConfirm={range => {
                      onChangeDateRange?.(range.startDate, range.endDate);
                    }}
                  />
                </div>
              </div>
              <Tabs
                className={cn(styles.myTeamsTab)}
                activeKey={tier}
                activeLineMode="fixed"
                onChange={key => onChangeTier?.(key as string)}>
                {tabOptions.map(option => (
                  <Tabs.Tab key={option.key} title={option.title} />
                ))}
              </Tabs>
              {/* 搜索框 */}
              <div className="p-3 bg-white">
                <SearchInput
                  value={searchValue || ''}
                  onChange={onSearchChange}
                  onSearch={onSearch}
                  onAutoSearch={onAutoSearch}
                  placeholder={t('agent.team-report.search-text')}
                  hasSearchButton
                  disableSearch={!searchValue || searchValue.length <= 0}
                  isSearching={isSearching}
                  className={styles.searchInput}
                />
              </div>
              {/* 提示信息 */}
              {showMinCharsWarning ? (
                <div className="px-3 text-sm text-[#F15802] text-center">
                  {t('agent.team-report.search-min-chars')}
                </div>
              ) : searching && resultCount && resultCount > 0 ? (
                <div className="px-3 text-sm text-t1 text-center">
                  <Trans
                    i18nKey="agent.foundResults"
                    values={{count: resultCount}}
                    components={{span: <span className="text-c1" />}}
                  />
                </div>
              ) : null}
            </>
          }
          headTitles={headTitles}
          onChangeSort={_onChangeSort}
          onScroll={_onScroll}
          isEmpty={isEmpty}
        />
      </>
    );
  },
);

export default MyTeams;
